import { Capacitor } from '@capacitor/core';
import { NativeAudio } from '@capgo/native-audio';
import { shouldUseNativeAudio, getAudioConfig, getPlatform } from '../utils/audioHelper';

export interface AudioAsset {
  assetId: string;
  assetPath: string;
  isUrl: boolean;
}

class NativeAudioService {
  private static instance: NativeAudioService;
  private isInitialized = false;
  private isNativeAvailable = false;
  private preloadedAssets = new Set<string>();
  private htmlAudioElements = new Map<string, HTMLAudioElement>();
  private currentPlayingId: string | null = null;

  private constructor() {}

  static getInstance(): NativeAudioService {
    if (!NativeAudioService.instance) {
      NativeAudioService.instance = new NativeAudioService();
    }
    return NativeAudioService.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Check if we should use native audio
      this.isNativeAvailable = shouldUseNativeAudio();
      const platform = getPlatform();

      if (this.isNativeAvailable) {
        // Get platform-specific configuration
        const config = getAudioConfig() as any;
        
        // Configure native audio for iOS and Android
        await NativeAudio.configure({ 
          focus: config.focus || false,
          fade: false
        });
      } else {
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize native audio:', error);
      this.isNativeAvailable = false;
      this.isInitialized = true;
    }
  }

  async preloadAudio(assetId: string, assetPath: string, isUrl: boolean = true): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Skip if already preloaded
    if (this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        // Use native audio for iOS/Android
        const config = getAudioConfig() as any;
        
        try {
          await NativeAudio.preload({
            assetId,
            assetPath,
            audioChannelNum: config.audioChannelNum || 1,
            isUrl,
            volume: 1.0
          });
          this.preloadedAssets.add(assetId);
        } catch (nativeError: any) {
          console.error(`Native audio preload failed for ${assetId}:`, nativeError);
          console.error(`Error details:`, {
            code: nativeError.code,
            message: nativeError.message,
            url: assetPath
          });
          
          // Fallback to HTML5 audio on native failure
          this.isNativeAvailable = false;
          
          // Retry with HTML5 audio
          await this.preloadAudio(assetId, assetPath, isUrl);
          return;
        }
      } else {
        // Fallback to HTML5 audio
        const audio = new Audio();
        audio.preload = 'auto';
        audio.src = assetPath;
        
        // iOS-specific attributes
        if (Capacitor.getPlatform() === 'ios') {
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
          audio.setAttribute('x-webkit-airplay', 'allow');
        }

        await new Promise<void>((resolve, reject) => {
          const handleCanPlay = () => {
            audio.removeEventListener('canplaythrough', handleCanPlay);
            audio.removeEventListener('error', handleError);
            this.htmlAudioElements.set(assetId, audio);
            this.preloadedAssets.add(assetId);
            resolve();
          };

          const handleError = (e: Event) => {
            audio.removeEventListener('canplaythrough', handleCanPlay);
            audio.removeEventListener('error', handleError);
            console.error(`Failed to preload HTML5 audio ${assetId}:`, e);
            reject(new Error(`Failed to preload audio: ${assetId}`));
          };

          audio.addEventListener('canplaythrough', handleCanPlay);
          audio.addEventListener('error', handleError);
          audio.load();
        });
      }
    } catch (error) {
      console.error(`Failed to preload audio ${assetId}:`, error);
      throw error;
    }
  }

  async play(assetId: string, options?: { loop?: boolean; volume?: number }): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      throw new Error(`Audio asset ${assetId} not preloaded`);
    }

    try {
      // Stop currently playing audio if any
      if (this.currentPlayingId && this.currentPlayingId !== assetId) {
        await this.stop(this.currentPlayingId);
      }

      if (this.isNativeAvailable) {
        await NativeAudio.play({ assetId });
        
        if (options?.loop) {
          await NativeAudio.loop({ assetId });
        }
        
        if (options?.volume !== undefined) {
          await NativeAudio.setVolume({ assetId, volume: options.volume });
        }
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (!audio) {
          throw new Error(`Audio element not found for ${assetId}`);
        }

        audio.loop = options?.loop || false;
        audio.volume = options?.volume ?? 1.0;
        
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
      }

      this.currentPlayingId = assetId;
    } catch (error) {
      console.error(`Failed to play audio ${assetId}:`, error);
      throw error;
    }
  }

  async pause(assetId: string): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        await NativeAudio.pause({ assetId });
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.pause();
        }
      }
    } catch (error) {
      console.error(`Failed to pause audio ${assetId}:`, error);
    }
  }

  async resume(assetId: string): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        await NativeAudio.resume({ assetId });
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }
      }
    } catch (error) {
      console.error(`Failed to resume audio ${assetId}:`, error);
    }
  }

  async stop(assetId: string): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        await NativeAudio.stop({ assetId });
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      }

      if (this.currentPlayingId === assetId) {
        this.currentPlayingId = null;
      }

    } catch (error) {
      console.error(`Failed to stop audio ${assetId}:`, error);
    }
  }

  async seek(assetId: string, timeInSeconds: number): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        // Native audio plugin doesn't support seek directly
        // We'll need to stop and replay from position
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.currentTime = timeInSeconds;
        }
      }
    } catch (error) {
      console.error(`Failed to seek audio ${assetId}:`, error);
    }
  }

  async setVolume(assetId: string, volume: number): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    const clampedVolume = Math.max(0, Math.min(1, volume));

    try {
      if (this.isNativeAvailable) {
        await NativeAudio.setVolume({ assetId, volume: clampedVolume });
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.volume = clampedVolume;
        }
      }
    } catch (error) {
      console.error(`Failed to set volume for ${assetId}:`, error);
    }
  }

  async setPlaybackRate(assetId: string, rate: number): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        // Native audio plugin might not support playback rate
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.playbackRate = rate;
        }
      }
    } catch (error) {
      console.error(`Failed to set playback rate for ${assetId}:`, error);
    }
  }

  getCurrentTime(assetId: string): number {
    if (!this.preloadedAssets.has(assetId)) {
      return 0;
    }

    if (!this.isNativeAvailable) {
      const audio = this.htmlAudioElements.get(assetId);
      if (audio) {
        return audio.currentTime;
      }
    }

    // Native audio doesn't provide current time directly
    return 0;
  }

  getDuration(assetId: string): number {
    if (!this.preloadedAssets.has(assetId)) {
      return 0;
    }

    if (!this.isNativeAvailable) {
      const audio = this.htmlAudioElements.get(assetId);
      if (audio) {
        return audio.duration || 0;
      }
    }

    // Native audio doesn't provide duration directly
    return 0;
  }

  async unload(assetId: string): Promise<void> {
    if (!this.preloadedAssets.has(assetId)) {
      return;
    }

    try {
      if (this.isNativeAvailable) {
        await NativeAudio.unload({ assetId });
      } else {
        const audio = this.htmlAudioElements.get(assetId);
        if (audio) {
          audio.pause();
          audio.src = '';
          this.htmlAudioElements.delete(assetId);
        }
      }

      this.preloadedAssets.delete(assetId);
      
      if (this.currentPlayingId === assetId) {
        this.currentPlayingId = null;
      }

    } catch (error) {
      console.error(`Failed to unload audio ${assetId}:`, error);
    }
  }

  async unloadAll(): Promise<void> {
    const assetIds = Array.from(this.preloadedAssets);
    
    for (const assetId of assetIds) {
      await this.unload(assetId);
    }
  }

  isPreloaded(assetId: string): boolean {
    return this.preloadedAssets.has(assetId);
  }

  isNative(): boolean {
    return this.isNativeAvailable;
  }
}

export const nativeAudioService = NativeAudioService.getInstance();